import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Hammer, Trees, Building2, Wrench, CheckCircle2, Phone } from "lucide-react";
import { COMPANY } from "@/data/site";
import { SERVICES } from "@/data/services";
import QuoteForm from "@/components/QuoteForm";
import Testimonials from "@/components/Testimonials";

const ICONS = {
  "render-insulation": Wrench,
  "domestic-extensions": Hammer,
  "commercial": Building2,
  "landscaping": Trees,
};

const Home = () => (
  <div data-testid="home-page">
    {/* HERO */}
    <section className="relative overflow-hidden bg-gradient-to-b from-crest-green-100/40 to-white">
      <div className="mx-auto max-w-7xl px-6 pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-crest-green/20 bg-white px-3 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-crest-green">
            <span className="h-1.5 w-1.5 rounded-full bg-crest-gold" /> Building with Integrity & Expertise
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-crest-navy leading-[1.05]">
            North West construction<br/>
            done <span className="text-crest-green">properly</span>.
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground font-body leading-relaxed">
            Cresttor Construction Ltd is a Liverpool-based building contractor delivering external render &amp; insulation, domestic extensions, commercial fit-outs and landscaping. New name &mdash; <span className="text-crest-navy font-medium">three decades</span> of trade experience behind every project.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/contact#quote"
              className="inline-flex items-center gap-2 rounded-md bg-crest-green px-7 py-3.5 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200"
              data-testid="hero-quote-cta"
            >
              Get a free estimate <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-2 rounded-md border border-crest-navy/20 bg-white px-7 py-3.5 text-sm font-semibold text-crest-navy hover:border-crest-green hover:text-crest-green transition-colors duration-200"
              data-testid="hero-call-cta"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>

          <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { v: "30+", l: "Years experience" },
              { v: "100%", l: "Free estimates" },
              { v: "5★",  l: "Client-rated team" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-crest-gold pl-4">
                <dt className="font-display text-3xl md:text-4xl font-bold text-crest-navy">{s.v}</dt>
                <dd className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{s.l}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 bg-crest-green rounded-lg -rotate-2" aria-hidden />
            <div className="absolute -inset-3 border-2 border-crest-gold rounded-lg rotate-1" aria-hidden />
            <img
              src={COMPANY.staffPortrait}
              alt="Cresttor Construction site lead in branded helmet & polo"
              className="relative rounded-lg w-full h-auto object-cover aspect-[4/5] shadow-xl"
              data-testid="hero-portrait"
            />
            <div className="absolute -bottom-5 -left-5 bg-white border border-border rounded-md px-5 py-3 shadow-lg flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-crest-green" />
              <div>
                <div className="font-display text-sm font-semibold text-crest-navy">Fully insured</div>
                <div className="text-xs text-muted-foreground">£5m public liability</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* INTRO */}
    <section className="mx-auto max-w-7xl px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Who we are</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-crest-navy leading-tight">
          A new name. <br /> A trusted crew.
        </h2>
        <hr className="gold-rule mt-6" />
      </div>
      <div className="lg:col-span-7 space-y-5 text-base text-muted-foreground font-body leading-relaxed">
        <p>
          Cresttor Construction Ltd was founded in Liverpool by a team of long-serving construction professionals — surveyors, render specialists, joiners, retrofit coordinators and project managers — who have been quietly delivering high-quality work across the North West for the better part of three decades.
        </p>
        <p>
          We believe construction should be straightforward: clear pricing, polite trades, clean sites, honest timelines. Whether it’s a single-storey extension, a render of an entire street or the fit-out of a busy retail unit, we treat every project with the same care.
        </p>
        <p>
          Based in Liverpool, working actively across the North West, North Wales and the Midlands.
        </p>
        <Link to="/about" className="inline-flex items-center gap-2 mt-3 text-crest-green font-semibold hover:text-crest-green-700 transition-colors duration-200" data-testid="about-link-home">
          More about Cresttor <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>

    {/* SERVICES — bento */}
    <section className="bg-crest-surface border-y border-border">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">What we do</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-crest-navy">Four specialisms. <br/>One contractor.</h2>
          </div>
          <p className="md:max-w-md text-muted-foreground font-body">
            We keep our scope deliberately focused — the four things our crew does to a high standard, every time.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-12">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.slug];
            const big = idx === 0;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className={`group relative overflow-hidden rounded-lg border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${big ? "md:col-span-6 md:row-span-2" : "md:col-span-3"}`}
              >
                <div className={`relative ${big ? "aspect-[4/3]" : "aspect-square"} overflow-hidden`}>
                  <img src={s.image} alt={s.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-crest-navy/85 via-crest-navy/20 to-transparent" />
                  <div className="absolute top-4 left-4 h-10 w-10 grid place-items-center rounded-md bg-white/95 text-crest-green">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="absolute bottom-0 left-0 p-5 md:p-6 text-white">
                    <h3 className="font-display text-lg md:text-xl font-semibold">{s.name}</h3>
                    {big && <p className="mt-2 text-sm text-white/85 max-w-md font-body">{s.short}</p>}
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-crest-gold opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore service <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
          <div className="md:col-span-6 rounded-lg bg-crest-green text-white p-8 md:p-10 flex flex-col justify-between">
            <div>
              <Award className="h-8 w-8 text-crest-gold" />
              <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold leading-tight">
                Backed by 30 years of trade craft
              </h3>
              <p className="mt-3 text-white/80 font-body max-w-md">
                Our directors and lead trades have built renders, extensions and commercial schemes across the North West since the 1990s. New banner — same hands.
              </p>
            </div>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 self-start rounded-md border border-crest-gold/60 px-5 py-3 text-sm font-semibold text-crest-gold hover:bg-crest-gold hover:text-crest-navy transition-colors duration-200" data-testid="about-cta-services">
              Read our story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* WHY US */}
    <section className="mx-auto max-w-7xl px-6 py-20 md:py-28">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Why Cresttor</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-crest-navy">A grown-up way to build.</h2>
          <p className="mt-5 text-muted-foreground font-body max-w-lg">
            We pair a small, experienced crew with mature processes — so jobs don’t drift, sites stay tidy and clients always know exactly what’s happening on theirs.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { t: "Free, written estimates",    d: "Itemised, fixed-price quotes wherever practical. No surprises mid-project." },
              { t: "Single point of contact",    d: "One project manager from the first survey to the snagging walk-around." },
              { t: "Insured & compliant",        d: "£5m public liability, employers’ liability, full RAMS for commercial works." },
              { t: "Funded scheme experience",   d: "PAS 2035 awareness for ECO/GBIS-funded retrofit and EWI projects." },
            ].map((b) => (
              <li key={b.t} className="flex gap-4">
                <CheckCircle2 className="h-6 w-6 text-crest-green shrink-0 mt-0.5" />
                <div>
                  <div className="font-display text-base font-semibold text-crest-navy">{b.t}</div>
                  <div className="text-sm text-muted-foreground font-body">{b.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative">
          <img src={COMPANY.brandedVan}
               alt="Cresttor Construction branded van, signage and letterhead"
               className="rounded-lg border border-border w-full"
               data-testid="brand-collateral-image" />
          <div className="absolute -bottom-6 right-6 bg-crest-navy text-white rounded-md px-5 py-4 max-w-xs shadow-lg">
            <div className="text-xs uppercase tracking-[0.18em] text-crest-gold">Service area</div>
            <div className="mt-1 font-display text-base">{COMPANY.area}</div>
          </div>
        </div>
      </div>
    </section>

    <Testimonials />

    {/* QUOTE CTA */}
    <section id="quote" className="bg-crest-navy text-white">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Free estimates</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold leading-tight">
            Tell us about your<br/>project.
          </h2>
          <p className="mt-5 text-white/75 font-body max-w-md">
            Drop in a few details and our team will call you back within one working day to arrange a no-obligation site visit.
          </p>
          <div className="mt-8 space-y-3 text-sm font-body text-white/85">
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-crest-gold" /> {COMPANY.phone}</div>
            <div className="text-white/60 text-xs uppercase tracking-[0.18em]">Or call us directly during office hours.</div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <QuoteForm variant="inverse" title="Request your free quote" subtitle="No call-centre, no chasing — direct line to our office." />
        </div>
      </div>
    </section>
  </div>
);

export default Home;
